/**
 * CONFIGURACIÓN DE FLUJO DE STOCK Y COMPRAS PENDIENTES
 * 
 * Este archivo centraliza todas las configuraciones del nuevo flujo
 * para poder activar/desactivar funcionalidades sin tocar el código.
 * 
 * ⚠️ IMPORTANTE: Mantener los valores por defecto hasta que todo esté probado.
 */

export const FLUJO_STOCK_CONFIG = {
  // ============================================================================
  // FEATURE FLAGS - Activar/Desactivar funcionalidades
  // ============================================================================
  
  /**
   * Habilitar el nuevo flujo de validación de stock
   * Si es false, todo funciona como antes (solo tiene_stock booleano)
   */
  VALIDACION_STOCK_HABILITADA: true, // ← ACTIVADO: Backend implementado
  
  /**
   * Habilitar reserva automática de stock cuando hay suficiente
   */
  RESERVA_STOCK_HABILITADA: true, // ← ACTIVADO
  
  /**
   * Habilitar envío automático a Compras Pendientes cuando hay faltantes
   */
  ENVIO_COMPRAS_PENDIENTES_HABILITADO: true, // ← ACTIVADO
  
  /**
   * Habilitar rollback si falla el envío a Compras Pendientes
   */
  ROLLBACK_TRANSACCIONAL_HABILITADO: true, // ← ACTIVADO
  
  // ============================================================================
  // CONFIGURACIÓN DE STOCK
  // ============================================================================
  
  /**
   * Campo que indica el stock físico real en el almacén
   * El backend debe exponer este campo en la API de artículos
   */
  CAMPO_STOCK_FISICO: 'stock_fisico', // o 'stock_actual', 'cantidad_disponible'
  
  /**
   * Campo que indica el stock reservado por otras cotizaciones
   * El backend debe exponer este campo
   */
  CAMPO_STOCK_RESERVADO: 'stock_reservado',
  
  /**
   * Campo calculado: stock disponible real
   * stock_disponible = stock_fisico - stock_reservado
   */
  CAMPO_STOCK_DISPONIBLE_CALCULADO: 'stock_disponible_real',
  
  // ============================================================================
  // CONFIGURACIÓN DE VALIDACIÓN
  // ============================================================================
  
  /**
   * Margen de seguridad para compras (multiplicador)
   * Si faltan 10 unidades, pedir: 10 * FACTOR_COMPRA = cantidad_a_comprar
   */
  FACTOR_COMPRA: 1.0, // 1.0 = igual a faltante, 1.1 = 10% más, etc.
  
  /**
   * Prioridad por defecto para solicitudes desde cotizaciones
   */
  PRIORIDAD_DEFAULT: 'ALTA',
  
  /**
   * Estados de cotización que activan validación de stock
   */
  ESTADOS_VALIDACION: ['aceptada', 'por_facturar', 'en_proceso'],
  
  // ============================================================================
  // ENDPOINTS DEL BACKEND
  // ============================================================================
  
  /**
   * Endpoint para verificar stock de múltiples artículos
   * POST /api/cotizaciones/verificar-stock
   * Body: { articulos: [{ articulo_id, cantidad_solicitada }] }
   */
  ENDPOINT_VERIFICAR_STOCK: '/cotizaciones/verificar-stock',
  
  /**
   * Endpoint para reservar stock (cuando hay suficiente)
   * POST /api/cotizaciones/{id}/reservar-stock
   */
  ENDPOINT_RESERVAR_STOCK: (cotizacionId) => `/cotizaciones/${cotizacionId}/reservar-stock`,
  
  /**
   * Endpoint para reverter reservas (al editar cotización)
   * POST /api/cotizaciones/{id}/revertir-reservas
   */
  ENDPOINT_REVERTIR_RESERVAS: (cotizacionId) => `/cotizaciones/${cotizacionId}/revertir-reservas`,
  
  /**
   * Endpoint para procesar cotización completa (transaccional)
   * POST /api/cotizaciones/{id}/procesar-con-stock
   * Body: { accion: 'crear' | 'editar' | 'aceptar' }
   */
  ENDPOINT_PROCESAR_COTIZACION: (cotizacionId) => `/cotizaciones/${cotizacionId}/procesar-con-stock`,
  
  // ============================================================================
  // LOGS Y DEBUG
  // ============================================================================
  
  /**
   * Nivel de logging: 'none' | 'error' | 'warn' | 'info' | 'debug'
   */
  LOG_LEVEL: 'info',
  
  /**
   * Habilitar logs detallados en consola
   */
  DEBUG_MODE: true,
  
  // ============================================================================
  // MENSAJES DE USUARIO
  // ============================================================================
  
  MENSAJES: {
    STOCK_SUFICIENTE: 'Cotización procesada. Stock reservado correctamente.',
    STOCK_INSUFICIENTE: 'Cotización aceptada con observación: hay artículos sin stock suficiente. Se han enviado solicitudes al área de compras.',
    ERROR_RESERVA: 'Error al reservar stock. La cotización no ha sido procesada.',
    ERROR_COMPRAS_PENDIENTES: 'Error al enviar solicitudes de compra. Por favor intente nuevamente.',
    EDICION_CON_FALTANTES: 'La edición generó nuevos artículos sin stock. Se han enviado solicitudes adicionales a compras.',
  }
};


/**
 * Función helper para verificar si una funcionalidad está habilitada
 * de forma segura (evita errores si el config no existe)
 */
export const isFeatureEnabled = (featureName) => {
  try {
    return FLUJO_STOCK_CONFIG[featureName] === true;
  } catch (e) {
    return false;
  }
};


/**
 * Logger condicional basado en configuración
 */
export const stockLogger = {
  debug: (...args) => FLUJO_STOCK_CONFIG.DEBUG_MODE && console.log('[FLUJO-STOCK]', ...args),
  info: (...args) => ['info', 'debug'].includes(FLUJO_STOCK_CONFIG.LOG_LEVEL) && console.info('[FLUJO-STOCK]', ...args),
  warn: (...args) => ['warn', 'info', 'debug'].includes(FLUJO_STOCK_CONFIG.LOG_LEVEL) && console.warn('[FLUJO-STOCK]', ...args),
  error: (...args) => ['error', 'warn', 'info', 'debug'].includes(FLUJO_STOCK_CONFIG.LOG_LEVEL) && console.error('[FLUJO-STOCK]', ...args),
};


export default FLUJO_STOCK_CONFIG;
